import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { LogoWall } from '../components/LogoWall';
import { ClosingCTA } from '../components/ClosingCTA';
import { EnterpriseUseCases } from '../components/EnterpriseUseCases';
import { EnterpriseIndustries } from '../components/EnterpriseIndustries';
import { EnterpriseConnections } from '../components/EnterpriseConnections';
import { EnterpriseBrain } from '../components/EnterpriseBrain';
import { ShieldCheck, Lock, FileKey, Database, Users, Activity, Headset, BookOpen, Zap } from 'lucide-react';
import { TALK_TO_SALES_URL } from '../lib/links';
const SECURITY_FEATURES = [{
  title: 'SOC 2 Type II',
  description: 'Independently audited and certified for security, availability, and confidentiality.',
  icon: ShieldCheck
}, {
  title: 'HIPAA Compliant',
  description: 'Ready for healthcare. We sign BAAs and ensure PHI is handled with strict compliance.',
  icon: Activity
}, {
  title: 'GDPR Ready',
  description: 'Full compliance with EU data protection regulations, including data residency options.',
  icon: Lock
}, {
  title: 'SSO & SAML',
  description: 'Integrate with Okta, Google Workspace, Azure AD, and other major identity providers.',
  icon: FileKey
}, {
  title: 'Audit Logs',
  description: 'Comprehensive logging of all workspace activity, exports, and administrative changes.',
  icon: Database
}, {
  title: 'Zero Model Training',
  description: 'Your data is yours. We never use enterprise customer data to train our AI models.',
  icon: Users
}];
const SUPPORT_FEATURES = [{
  title: 'Dedicated Success Manager',
  description: 'A dedicated partner to help you map workflows, train your team, and drive adoption.',
  icon: Users
}, {
  title: 'Custom Onboarding',
  description: 'Tailored rollout plans, white-glove setup, and live training sessions for your org.',
  icon: BookOpen
}, {
  title: '24/7 Priority Support',
  description: 'Skip the queue. Get guaranteed SLAs and direct access to our engineering team.',
  icon: Headset
}];
export function Enterprise() {
  return <div className="min-h-screen bg-white font-body selection:bg-[#EA580C]/20 selection:text-[#333333]" data-id="element-9463" data-mp="32h98okfve1h0">
      {/* Hero Section */}
      <section className="relative w-full bg-[#FAFAFA] pt-40 pb-32 md:pb-48 overflow-hidden flex flex-col items-center justify-center" data-id="element-9464" data-mp="2nl9semilwv0s">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 relative z-10 text-center" data-id="element-9465" data-mp="3g8dzqy569lfq">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="flex flex-col items-center" data-id="element-9466" data-mp="3gaqhpbaxjvss">
            {/* Eyebrow Pill */}
            <div className="inline-flex items-center gap-2 bg-[#FFEDD5]/70 border border-[#EA580C]/20 px-4 py-1.5 rounded-full mb-8" data-id="element-9467" data-mp="3bg31s0tzs9pt">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#EA580C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-9468" data-mp="3016fojz9iux">
                <path d="M12 2L15 9L22 12L15 15L12 22L9 15L2 12L9 9L12 2Z" data-id="element-9469" data-mp="1sh55fg88fbzy" />
              </svg>
              <span className="text-sm font-medium text-[#333333]" data-id="element-9470" data-mp="1njdb6fk47dsl">
                <span className="font-bold" data-id="element-9471" data-mp="2292kwz69wq4h">Giga</span> Enterprise
              </span>
            </div>

            {/* Headline */}
            <h1 className="font-title text-5xl md:text-7xl lg:text-[80px] font-bold tracking-tight text-[#333333] mb-8 leading-[1.05] max-w-5xl mx-auto" data-id="element-9472" data-mp="1xahrbilj14p0">
              Give everyone on your team a shared brain.
            </h1>

            {/* Subhead (Gray text) */}
            <p className="text-xl md:text-2xl text-[#475569] mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-9473" data-mp="1ilv12lud2sj0">
              One AI brain that connects to everything, works for every
              department, and meets the security and governance bar your
              organization demands.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-9474" data-mp="2py3uwqur89bv">
              <a href={TALK_TO_SALES_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-[#EA580C] hover:bg-[#C2410C] text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg shadow-[#EA580C]/20" data-id="element-9475" data-mp="3la4ozkpron68">
                Contact Sales
              </a>
              <Link to="/unlisted/security" className="inline-flex items-center gap-1.5 text-[#333333] font-semibold px-4 py-4 hover:text-[#EA580C] transition-colors" data-id="element-9476" data-mp="30uf22z7nym1">
                View security docs
                <span aria-hidden="true" data-id="element-9477" data-mp="c1irwucvbsxc">→</span>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* SaaS-y Decorative Bottom Elements (Faint Cards) */}
        <div className="absolute bottom-0 left-0 w-full h-32 pointer-events-none overflow-hidden flex items-end justify-center gap-4 opacity-40" data-id="element-9478" data-mp="lsn7nwadbht8">
          {/* Fade mask so they blend smoothly into the background */}
          <div className="absolute inset-0 bg-gradient-to-t from-transparent to-[#FAFAFA] z-10" data-id="element-9479" data-mp="vp8emn5y72zp" />

          {/* Row of faint decorative cards */}
          {[...Array(12)].map((_, i) => <div key={i} className="w-32 h-24 bg-white border border-neutral-200 rounded-t-2xl shadow-sm shrink-0 relative overflow-hidden" data-id="element-9480" data-mp="2b8zcmhkobla3" data-mpi={i}>
              {/* Subtle diagonal lines inside the cards for extra texture */}
              <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, #000 0, #000 1px, transparent 0, transparent 50%)',
            backgroundSize: '10px 10px'
          }} data-id="element-9481" data-mp="21equmnt9raq6" />
            </div>)}
        </div>
      </section>

      {/* Logo Wall */}
      <LogoWall data-id="element-9482" data-mp="2kb8iuv8wssho" />

      {/* Built for every team — org-wide use-case explorer */}
      <EnterpriseUseCases data-id="element-9483" data-mp="2k95tmdfkz3e0" />

      {/* Supports every industry — editorial vertical band */}
      <EnterpriseIndustries data-id="element-9484" data-mp="3hrxkooxuzur2" />

      {/* Connects to everything — system connections */}
      <EnterpriseConnections data-id="element-9485" data-mp="3cycpf4nnhevk" />

      {/* GigaBrain core — the engine + memory */}
      <EnterpriseBrain data-id="element-9486" data-mp="1b3osn19lvstx" />

      {/* Security & Compliance Grid */}
      <section id="security" className="w-full bg-[#FAF8F4] py-28 md:py-40 border-b border-neutral-200 scroll-mt-24" data-id="element-9487" data-mp="1g1yntbvbytfj">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-9488" data-mp="11bj1ftr6hffs">
          <div className="text-center mb-16 md:mb-20" data-id="element-9489" data-mp="16bnhxjs36a60">
            <span className="text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-5 block" data-id="element-9490" data-mp="16a2o5wdliv97">
              Security First
            </span>
            <h2 className="text-4xl md:text-5xl font-title font-bold text-[#333333] mb-6 tracking-tight max-w-3xl md:max-w-4xl mx-auto" data-id="element-9491" data-mp="wd7ubqs9pj19">
              Bank-grade security, by default.
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto" data-id="element-9492" data-mp="ncjgaw2vjiod">
              We built Giga from the ground up to protect your most sensitive
              company data. Your context stays yours.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-id="element-9493" data-mp="2hphmp4lgdv6f">
            {SECURITY_FEATURES.map((feature, i) => {
            const Icon = feature.icon;
            return <motion.div key={feature.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-white rounded-2xl p-6 md:p-8 border border-neutral-200 shadow-sm flex flex-col" data-id="element-9494" data-mp="15f8r6xlcneu" data-mpi={i}>
                  <div className="w-12 h-12 rounded-xl bg-[#FFEDD5] text-[#EA580C] flex items-center justify-center mb-6" data-id="element-9495" data-mp="july3csw4d9m">
                    <Icon size={24} data-id="element-9496" data-mp="tt1nnbc9reh0" />
                  </div>
                  <h3 className="text-xl font-bold text-[#333333] mb-3" data-id="element-9497" data-mp="ttkthtonaceo">
                    {feature.title}
                  </h3>
                  <p className="text-neutral-600 leading-relaxed" data-id="element-9498" data-mp="30lc6g0s7ru8r">
                    {feature.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Governance & Control (Side by Side) */}
      <section className="w-full bg-white py-28 md:py-40 border-b border-neutral-200 overflow-hidden" data-id="element-9499" data-mp="2eor2ob8yk723">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-9500" data-mp="2jhk7t08c9hep">
          <div className="flex flex-col lg:flex-row items-center gap-16 md:gap-24" data-id="element-9501" data-mp="2ogenvuex8rv4">
            {/* Text Content */}
            <div className="flex-1 max-w-xl" data-id="element-9502" data-mp="1qtodu3a5m3lm">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} data-id="element-9503" data-mp="1qupmk13p6e06">
                <span className="text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-5 block" data-id="element-9504" data-mp="24q1l6jkz4omd">
                  Admin Controls
                </span>
                <h2 className="text-4xl md:text-5xl font-title font-bold text-[#333333] mb-6 leading-[1.1] tracking-tight" data-id="element-9505" data-mp="3g5zyx0r3jytx">
                  Total control over your workspace.
                </h2>
                <p className="text-lg text-neutral-600 leading-relaxed mb-8" data-id="element-9506" data-mp="10jnmwl7ib873">
                  Manage how your team uses Giga with granular permissions,
                  custom data retention policies, and deep visibility into
                  usage.
                </p>
                <ul className="space-y-5" data-id="element-9507" data-mp="ltzlzl38mbjw">
                  {['Role-Based Access Control (RBAC)', 'Custom Data Retention Policies', 'Workspace Usage Analytics', 'Domain Capture & Auto-Provisioning'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-9508" data-mp="cs9uygd9pemn" data-mpi={i}>
                      <div className="w-6 h-6 rounded-full bg-[#FFEDD5] text-[#EA580C] flex items-center justify-center shrink-0" data-id="element-9509" data-mp="cumjxdhg0p97">
                        <Zap size={14} data-id="element-9510" data-mp="2772om9ulbvby" />
                      </div>
                      <span className="text-[#333333] font-medium" data-id="element-9511" data-mp="274gf44o535r3">{item}</span>
                    </li>)}
                </ul>
              </motion.div>
            </div>

            {/* Abstract UI Mockup */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="flex-1 w-full" data-id="element-9512" data-mp="37lk2f62nohr5">
              <div className="w-full aspect-[4/3] rounded-3xl bg-[#FAF8F4] border border-neutral-200 p-8 shadow-inner flex flex-col gap-4" data-id="element-9513" data-mp="2swnncp26m4h1">
                {/* Mockup Header */}
                <div className="flex items-center justify-between border-b border-neutral-200 pb-4 mb-2" data-id="element-9514" data-mp="2oszf045q74uq">
                  <div className="flex items-center gap-3" data-id="element-9515" data-mp="32obk95hicdpw">
                    <div className="w-8 h-8 rounded bg-[#2B211A] flex items-center justify-center" data-id="element-9516" data-mp="2xuyx6zsh8ez9">
                      <ShieldCheck size={16} className="text-white" data-id="element-9517" data-mp="2a4v0zdhmbhia" />
                    </div>
                    <div className="font-bold text-[#333333]" data-id="element-9518" data-mp="256bi5lybmlu5">
                      Admin Console
                    </div>
                  </div>
                  <div className="w-24 h-8 rounded-full bg-neutral-200 animate-pulse" data-id="element-9519" data-mp="pfqzl7lyo56o" />
                </div>
                {/* Mockup Rows */}
                {[1, 2, 3].map(row => <div key={row} className="bg-white rounded-xl p-4 border border-neutral-100 shadow-sm flex items-center justify-between" data-id="element-9520" data-mp="bq73gkmvy66c">
                    <div className="flex items-center gap-4" data-id="element-9521" data-mp="14db1g5nk9k8m">
                      <div className="w-10 h-10 rounded-full bg-neutral-100 animate-pulse" data-id="element-9522" data-mp="14dt3jiwwmsog" />
                      <div className="space-y-2" data-id="element-9523" data-mp="18dtwe8kr36f1">
                        <div className="w-32 h-3 bg-neutral-200 rounded-full animate-pulse" data-id="element-9524" data-mp="ufddlj9m5pgj" />
                        <div className="w-20 h-2 bg-neutral-100 rounded-full animate-pulse" data-id="element-9525" data-mp="zdoqy3z4ai3i" />
                      </div>
                    </div>
                    <div className="w-16 h-6 rounded-full bg-[#FFEDD5] border border-[#EA580C]/20" data-id="element-9526" data-mp="3f3hyo8vttwcf" />
                  </div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Dedicated Support */}
      <section className="w-full bg-[#FAF8F4] py-28 md:py-40" data-id="element-9527" data-mp="2w0g6w37drygt">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-9528" data-mp="2w19ch0ulshxd">
          <div className="text-center mb-16 md:mb-20" data-id="element-9529" data-mp="2r8y5je1g3vvb">
            <span className="text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-5 block" data-id="element-9530" data-mp="2r7elrdgkz2os">
              Enterprise Support
            </span>
            <h2 className="text-4xl md:text-5xl font-title font-bold text-[#333333] mb-6 tracking-tight max-w-3xl md:max-w-4xl mx-auto" data-id="element-9531" data-mp="2hccv2d6npsk3">
              A true partner, not just a vendor.
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto" data-id="element-9532" data-mp="28bokxtkdwcch">
              We work closely with our enterprise customers to ensure successful
              rollouts and maximum ROI.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8" data-id="element-9533" data-mp="6hfqo4kmwo3l">
            {SUPPORT_FEATURES.map((feature, i) => {
            const Icon = feature.icon;
            return <motion.div key={feature.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-white rounded-2xl p-6 md:p-8 border border-neutral-200 shadow-sm text-center flex flex-col items-center" data-id="element-9534" data-mp="9358xxbcgfja" data-mpi={i}>
                  <div className="w-16 h-16 rounded-full bg-[#FAF8F4] border border-neutral-100 text-[#333333] flex items-center justify-center mb-6" data-id="element-9535" data-mp="3r9ba04dhpk6h">
                    <Icon size={28} data-id="element-9536" data-mp="1p4053at5mg" />
                  </div>
                  <h3 className="text-xl font-bold text-[#333333] mb-3" data-id="element-9537" data-mp="453u5okyb2dm">
                    {feature.title}
                  </h3>
                  <p className="text-neutral-600 leading-relaxed" data-id="element-9538" data-mp="1bhvt29pwbd33">
                    {feature.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <ClosingCTA data-id="element-9539" data-mp="193s287wp64yk" />
    </div>;
}